/**
 * Terminal store — V1 line-based shell into the user's sandbox.
 *
 * Singleton (not per-task), mirroring useBrowserStore: the underlying
 * sandbox is one shared resource per user, so the scrollback and
 * command history are shared too. The Computer panel just gives each
 * task a tab that views the same terminal.
 *
 * Wire protocol:
 *   runCommand(cmd) → emit TERMINAL_EXEC { id, command }
 *   handler.onResult(payload) → update matching entry, clear runningId
 *
 * Persistence: commandHistory only. Scrollback is in-memory.
 * Spec: docs/superpowers/specs/2026-05-26-terminal-tab-design.md
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { Socket } from "socket.io-client";
import {
  EVENTS,
  type TerminalExecPayload,
  type TerminalResultPayload,
} from "@bash/shared";

export const MAX_HISTORY = 100;
export const MAX_ENTRIES = 500;
/** Sandbox's documented default cwd — used as the initial prompt
 *  before the first command's result tells us the real cwd. */
export const DEFAULT_CWD = "/app/workspace";

export type EntryStatus = "running" | "done" | "error";

export interface TerminalEntry {
  id: string;
  command: string;
  status: EntryStatus;
  startedAt: number;
  /** Cwd that was active when this command ran. Stamped at runCommand
   *  time from currentCwd so scrollback shows the prompt path that
   *  was actually in effect — not the live cwd of the shell now. */
  cwd?: string;
  output?: string;
  exitCode?: number;
  durationMs?: number;
  truncated?: boolean;
  error?: string;
}

interface TerminalState {
  entries: TerminalEntry[];
  commandHistory: string[]; // index 0 = newest
  runningId: string | null;
  historyCursor: number | null;
  socket: Socket | null;
  /** Live shell cwd. Updated from TERMINAL_RESULT.cwd. Drives the
   *  active prompt and is stamped onto each new entry. */
  currentCwd: string;

  setSocket: (socket: Socket | null) => void;
  runCommand: (command: string) => void;
  onResult: (payload: TerminalResultPayload) => void;
  clear: () => void;
  setHistoryCursor: (delta: -1 | 1) => string | null;
}

function newId(): string {
  // Sandbox-safe UUID. `crypto.randomUUID` exists in modern browsers and Node 18+.
  // Fallback uses Math.random for older test environments.
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export const useTerminalStore = create<TerminalState>()(
  persist(
    (set, get) => ({
      entries: [],
      commandHistory: [],
      runningId: null,
      historyCursor: null,
      socket: null,
      currentCwd: DEFAULT_CWD,

      setSocket: (socket) => set({ socket }),

      runCommand: (raw) => {
        const command = raw.trim();
        if (!command) return;
        const state = get();
        if (state.runningId !== null) return;

        const id = newId();
        const entry: TerminalEntry = {
          id,
          command,
          status: "running",
          startedAt: Date.now(),
          cwd: state.currentCwd,
        };

        // Append entry, cap at MAX_ENTRIES (drop oldest).
        const nextEntries =
          state.entries.length >= MAX_ENTRIES
            ? [
                ...state.entries.slice(state.entries.length - MAX_ENTRIES + 1),
                entry,
              ]
            : [...state.entries, entry];

        // Update history: dedup against newest, cap.
        const nextHistory =
          state.commandHistory[0] === command
            ? state.commandHistory
            : [command, ...state.commandHistory].slice(0, MAX_HISTORY);

        set({
          entries: nextEntries,
          commandHistory: nextHistory,
          runningId: id,
          historyCursor: null,
        });

        const payload: TerminalExecPayload = { id, command };
        state.socket?.emit(EVENTS.TERMINAL_EXEC, payload);
      },

      onResult: (payload) => {
        const state = get();
        const idx = state.entries.findIndex((e) => e.id === payload.id);
        if (idx === -1) return;
        const isError = payload.exitCode === -1 || !!payload.error;
        const updated: TerminalEntry = {
          ...state.entries[idx],
          status: isError ? "error" : "done",
          output: payload.output,
          exitCode: payload.exitCode,
          durationMs: payload.durationMs,
          truncated: payload.truncated,
          // The wake-door credit gate (founder 09-03): the server refused to
          // exec (the box was not woken) with a typed reason — the line says
          // what happened in the user's words, never the code.
          error:
            payload.error === "out_of_credits"
              ? "Out of credits. Add credits to use the terminal."
              : payload.error,
        };
        const nextEntries = [...state.entries];
        nextEntries[idx] = updated;
        set({
          entries: nextEntries,
          runningId: state.runningId === payload.id ? null : state.runningId,
          // Advance the live cwd if the server told us about a new one.
          // Stale entries keep their stamped cwd — only the next prompt
          // reflects the change, matching real-shell semantics.
          currentCwd: payload.cwd ?? state.currentCwd,
        });
      },

      clear: () => set({ entries: [] }),

      setHistoryCursor: (delta) => {
        const state = get();
        const history = state.commandHistory;
        if (history.length === 0) return null;

        let nextCursor: number | null;
        if (state.historyCursor === null) {
          // Fresh navigation. -1 (up) seeds at 0 (newest). +1 (down) is a no-op.
          if (delta === 1) return null;
          nextCursor = 0;
        } else {
          const candidate = state.historyCursor + (delta === -1 ? 1 : -1);
          if (candidate < 0) {
            // Walking past newest — return to empty input.
            set({ historyCursor: null });
            return null;
          }
          // Walking past oldest — clamp.
          nextCursor = Math.min(candidate, history.length - 1);
        }
        set({ historyCursor: nextCursor });
        return history[nextCursor] ?? null;
      },
    }),
    {
      name: "vestra-terminal-history-v1",
      storage: createJSONStorage(() => localStorage),
      // Only persist the command history. entries, runningId, socket
      // are all session-bound and would be misleading after a refresh.
      partialize: (s) => ({ commandHistory: s.commandHistory }),
      version: 1,
    }
  )
);
